import {
    faClock,
    faEllipsisVertical,
    faPlus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Box,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    useTheme,
} from "@vapor/react-material";
import { Dayjs } from "dayjs";
import { capitalize } from "lodash";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { usePermission } from "../../../../../core/permissions/usePermission";
import { formatDate } from "../../../../../utils/dateUtils";
import { DeleteCustomFulfilment } from "../../../../components/delete-custom-fulfilment/DeleteCustomFulfilment";

interface FulfilmentUnLinkedProps {
    date: Dayjs;
    description: string;
    isCustomFulfilment: boolean;
    levyId: string;
    onClickLinkTaxPayers: (levyId?: string) => unknown;
    onClickEditCustomFulfilment?: (levyId?: string) => unknown;
    title: string;
    header?: boolean;
}

export const FulfilmentUnlinked = ({
    date,
    description,
    isCustomFulfilment,
    levyId,
    onClickLinkTaxPayers,
    onClickEditCustomFulfilment,
    title,
    header = false,
}: FulfilmentUnLinkedProps): ReactElement => {
    // states
    const theme = useTheme();
    const { t } = useTranslation();

    const canLinkTaxPayer = usePermission("taxPayerLink");
    const canEditCustomFulfilment = usePermission("fulfilmentEdit");
    const canDeleteCustomFulfilment = usePermission("fulfilmentDelete");

    const onClickMenuItemLinkTaxPayers = (
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.stopPropagation();
        onClickLinkTaxPayers(levyId);
    };

    const [fulfilmentTitleMaxWidth, setFulfilmentTitleMaxWidth] =
        useState("0px");
    const [fulfilmentLinkButtonId] = useState(v4());
    const [fulfilmentContainerId] = useState(v4());

    const [menuAnchorEl, setMenuAnchorEl] = useState<
        (EventTarget & HTMLButtonElement) | null
    >(null);

    const onClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(null);
    };

    const onClickMenuItemDeleteCustomFulfilment = (
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.stopPropagation();
    };

    const onClickMenuItemEditCustomFulfilment = (
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.stopPropagation();
    };

    // Items built once: the kebab trigger derives from them
    // (menuItems.length), so it cannot drift from what the menu holds.
    const menuItems = isCustomFulfilment
        ? [
              canEditCustomFulfilment && (
                  <MenuItem
                      key="edit"
                      onClick={(e) => {
                          handleCloseMenu(e);
                          onClickMenuItemEditCustomFulfilment(e);
                          if (onClickEditCustomFulfilment)
                              onClickEditCustomFulfilment();
                      }}
                  >
                      {t(
                          "views.home.components.fulfilment_non_linked.menu_items.edit",
                      )}
                  </MenuItem>
              ),
              canDeleteCustomFulfilment && (
                  <MenuItem
                      key="delete"
                      onClick={onClickMenuItemDeleteCustomFulfilment}
                  >
                      <DeleteCustomFulfilment
                          levyId={levyId}
                          onCancel={handleCloseMenu}
                          onDelete={handleCloseMenu}
                          sx={{ width: "100%" }}
                      >
                          {t(
                              "views.home.components.fulfilment_non_linked.menu_items.delete",
                          )}
                      </DeleteCustomFulfilment>
                  </MenuItem>
              ),
          ].filter(Boolean)
        : [];

    //TODO: refactor
    useEffect(() => {
        const handleResize = () => {
            const fulfilmentOffsetLeft =
                (document
                    .getElementById(fulfilmentContainerId)
                    ?.getBoundingClientRect().left || 0) * 2;
            const fulfilmentLinkButtonWidth =
                document.getElementById(fulfilmentLinkButtonId)?.clientWidth ||
                0;

            const widthVar = `calc(98vw - ${fulfilmentOffsetLeft}px - ${fulfilmentLinkButtonWidth}px)`;

            setFulfilmentTitleMaxWidth(widthVar);
        };
        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [fulfilmentContainerId, fulfilmentLinkButtonId]);

    return (
        <Stack
            direction="column"
            id={fulfilmentContainerId}
            sx={{
                border: "1px solid #B9C8D0",
                borderTop: "none",
            }}
        >
            {header && (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                        background: theme.palette.primary.background,
                        borderTop: "1px solid #B9C8D0",
                        paddingX: 2,
                        paddingY: 1,
                    }}
                >
                    <FontAwesomeIcon
                        icon={faClock}
                        color={theme.palette.primary.textTitleColor}
                    />
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        sx={{ lineHeight: 1 }}
                        variant="bodyLarge500"
                    >
                        {t("views.home.components.fulfilment.header.deadline")}
                    </Typography>
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        sx={{ lineHeight: 1 }}
                        variant="bodyLarge500"
                    >
                        {formatDate(date)}
                    </Typography>
                </Stack>
            )}
            <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ padding: 1.5 }}
            >
                <Stack maxWidth={fulfilmentTitleMaxWidth} sx={{ width: 1 }}>
                    <Stack
                        alignItems="center"
                        direction="row"
                        justifyContent="space-between"
                        minHeight={32}
                        mr={2}
                    >
                        <Stack
                            sx={{
                                cursor: "pointer",
                                display: "none",
                                width: 1,
                                ":hover": {
                                    width: "100%",
                                    color: "rgb(0, 144, 209)",
                                    display: "inline",
                                },
                                zIndex: "2",
                            }}
                            style={{
                                backgroundColor:
                                    theme.palette.primary.whiteSmoke,
                            }}
                        >
                            <Tooltip title={description}>
                                <Typography
                                    // variant="titleSmall"
                                    color={theme.palette.text.primary}
                                    noWrap
                                >
                                    {description}
                                </Typography>
                            </Tooltip>
                        </Stack>
                        <Typography
                            color={theme.palette.text.primary}
                            noWrap
                            variant="bodyLarge500"
                        >
                            {description}
                        </Typography>
                    </Stack>
                    <Box
                        sx={{
                            background: theme.palette.primary.background,
                            width: "fit-content",
                            padding: 1,
                            borderRadius: "4px",
                        }}
                    >
                        <Typography
                            color={theme.palette.primary.textSubduedColor}
                            variant="subtitle2"
                            sx={{
                                lineHeight: 1,
                            }}
                        >
                            {capitalize(title)}
                        </Typography>
                    </Box>
                </Stack>
                <Stack justifyContent="center" id={fulfilmentLinkButtonId}>
                    {canLinkTaxPayer && (
                        <Button
                            variant="outlined"
                            onClick={onClickMenuItemLinkTaxPayers}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <Typography ml={1}>
                                {t(
                                    "views.home.components.fulfilment_non_linked.actions.link",
                                    "Associa",
                                )}
                            </Typography>
                        </Button>
                    )}
                </Stack>
                {menuItems.length > 0 && (
                    <Stack direction="row" alignItems="center">
                        <IconButton
                            aria-controls="menu"
                            aria-haspopup="true"
                            onClick={onClickMenu}
                            size="small"
                        >
                            <FontAwesomeIcon
                                color={theme.palette.primary.interactiveDefault}
                                icon={faEllipsisVertical}
                                size="xs"
                            />
                        </IconButton>
                        <Menu
                            id="menu"
                            anchorEl={menuAnchorEl}
                            open={menuItems.length > 0 && !!menuAnchorEl}
                            onClose={handleCloseMenu}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            {menuItems}
                        </Menu>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};
